import { useState } from 'react';
import { useArenaReservations } from 'modules/arena/reservations/useArenaReservations';
import { StatTile } from 'shared/components/ui/StatTile';
import { PillTabs } from 'shared/components/ui/PillTabs';
import { Button } from 'shared/components/ui/Button';
import { ReservationRow } from 'modules/arena/reservations/components/ReservationRow';
import { ReservationDetailPanel } from 'modules/arena/reservations/components/ReservationDetailPanel';
import { NewReservationForm } from 'modules/arena/reservations/components/NewReservationForm';
import { RESERVATION_STATUS_FILTERS } from 'modules/arena/reservations/content';
import { AsyncSection } from 'shared/components/ui/LoadingState';
import { SkeletonListDetail, SkeletonPills, SkeletonStatGrid } from 'shared/components/ui/Skeleton';

export function ArenaReservationsPage() {
  const {
    reservations,
    allReservations,
    isLoading,
    error,
    statusFilter,
    setStatusFilter,
    selectedId,
    setSelectedId,
    selectedReservation,
    confirm,
    cancel,
    markNoShow,
    markCompleted,
    extendDeadline,
    chargeRemaining,
    createManualReservation,
  } = useArenaReservations();

  const [isCreating, setIsCreating] = useState(false);

  const pendingCount = allReservations.filter((r) => r.status === 'pending').length;
  const awaitingPaymentCount = allReservations.filter((r) => r.status === 'awaiting_payment').length;
  const confirmedTodayCount = allReservations.filter(
    (r) => r.status === 'confirmed' && r.dateLabel.startsWith('hoje')
  ).length;
  const attentionCount = allReservations.filter((r) => r.status === 'no_show' || r.status === 'cancelled').length;

  return (
    <div className="flex-1 p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-xl font-bold text-ink">Reservas</h1>
          <p className="text-sm text-ink-secondary">
            Todas as reservas da unidade, do pedido pendente até a conclusão.
          </p>
        </div>
        {!isCreating && <Button onClick={() => setIsCreating(true)}>+ Nova reserva</Button>}
      </div>

      <AsyncSection
        isLoading={isLoading}
        error={error}
        loadingLabel="Carregando reservas..."
        skeleton={
          <>
            <SkeletonStatGrid />
            <SkeletonPills className="mt-6" widths={[64, 96, 176, 112, 104, 104, 88, 80]} />
            <SkeletonListDetail />
          </>
        }
      >
          <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
            <StatTile label="Pendentes" value={String(pendingCount)} />
            <StatTile label="Aguardando pagamento" value={String(awaitingPaymentCount)} />
            <StatTile label="Confirmadas hoje" value={String(confirmedTodayCount)} />
            <StatTile label="No-show / canceladas" value={String(attentionCount)} />
          </div>

          {isCreating && (
            <div className="mt-6">
              <NewReservationForm
                onCreate={(input) => {
                  createManualReservation(input);
                  setIsCreating(false);
                }}
                onCancel={() => setIsCreating(false)}
              />
            </div>
          )}

          <div className="mt-6">
            <PillTabs options={RESERVATION_STATUS_FILTERS} value={statusFilter} onChange={setStatusFilter} />
          </div>

          <div className="mt-4 grid gap-6 lg:grid-cols-[1.1fr_1fr]">
            <div className="flex flex-col gap-3">
              {reservations.length === 0 ? (
                <p className="text-sm text-ink-secondary">Nenhuma reserva com esse status.</p>
              ) : (
                reservations.map((reservation) => (
                  <ReservationRow
                    key={reservation.id}
                    reservation={reservation}
                    isSelected={reservation.id === selectedId}
                    onSelect={setSelectedId}
                  />
                ))
              )}
            </div>

            <div className="lg:sticky lg:top-6 lg:self-start">
              {selectedReservation ? (
                <ReservationDetailPanel
                  reservation={selectedReservation}
                  onConfirm={confirm}
                  onCancel={cancel}
                  onMarkNoShow={markNoShow}
                  onMarkCompleted={markCompleted}
                  onExtendDeadline={extendDeadline}
                  onChargeRemaining={chargeRemaining}
                />
              ) : (
                <p className="text-sm text-ink-secondary">Selecione uma reserva para ver os detalhes.</p>
              )}
            </div>
          </div>
      </AsyncSection>
    </div>
  );
}
